import { rpx } from 'client/lib/rpx-client';
import { AuthForm } from '@course/components/checkout';
import { intl } from 'shared/intl/use-intl';
import { useCurrentUser, CurrentUser, useCurrentTenant } from 'client/lib/auth';
import { setUrlSearchParam } from 'client/utils/url';
import { redirectToCourse } from '@course/components/course-checkout';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defStudentRoute } from '@course/components/courses-app-router';
import { ComponentChildren, Ref } from 'preact';
import { BtnPrimary } from '@components/buttons';
import { useState, useRef } from 'preact/hooks';
import { TopBar } from '@course/components/top-bar';
import { IcoGift, IcoChevronDown, IcoCheckCircle } from '@components/icons';
import { useImageUrl } from 'client/utils/cdn';
import { GiftedCourse, SubCourse } from '@course/components/course-checkout/types';
import { OutlineModule } from '../guide-course-salespage/outline-block';
import { groupBy } from 'shared/utils';
import { showError } from '@components/app-error';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { courseLabel } from 'shared/terminology';

type GiftedCourseWithOutline = GiftedCourse & {
  modules: OutlineModule[];
};

interface AcceptGiftCardProps {
  forwardRef: Ref<HTMLDivElement>;
  showAuthForm: boolean;
  isLoading: boolean;
  courseLabel: string;
  onAcceptClick: () => void;
  onAuthSubmit(user: CurrentUser): Promise<unknown>;
}

interface GiftHeaderProps {
  onAcceptClick: () => void;
  isLoading: boolean;
  courseTitle: string;
  courseLabel: string;
  customMessage: string | undefined;
  fromUserName: string;
}

export const route = defStudentRoute({
  isPublic: true,
  load,
  Page,
});

async function load(route: RouteLoadProps) {
  const { courseId } = route.params;
  const giftCode = route.params.gift;

  const [result, courseOutlineResult] = await Promise.all([
    rpx.gifts.getGiftState({ courseId, gift: giftCode }),
    rpx.courses.getCourseOutline({ courseId }),
  ]);

  if (result.isOwner) {
    route.router.goto('/account/gifts');
  }

  const outline = courseOutlineResult[0];
  let courseModules: OutlineModule[] = [];
  if (outline) {
    const lessonsByModule = groupBy((l) => l.moduleId, outline.lessons);
    courseModules = outline.modules.map((mod) => ({
      ...mod,
      lessons: lessonsByModule[mod.id] || [],
    }));
  }
  return {
    course: {
      ...result.course,
      subcourses: result.subcourses,
      modules: courseModules,
    },
    giftCode,
    gift: result.gift,
  };
}

type Props = LoadedProps<typeof load>;

function Page(props: Props) {
  const user = useCurrentUser();
  const { course, gift, giftCode } = props.state;
  const [uiState, setUiState] = useState({
    isLoading: false,
    showAuthForm: false,
  });
  const acceptGiftCardRef = useRef<HTMLDivElement>(null);

  const handleAcceptClick = async () => {
    if (user) {
      try {
        setUiState((s) => ({ ...s, isLoading: true }));
        await rpx.gifts.acceptGift({ courseId: course.id, giftCode });
        await redirectToCourse(props.router, course);
      } catch (err) {
        showError(err);
      } finally {
        setUiState((s) => ({ ...s, isLoading: false }));
      }
    } else {
      setUiState((s) => ({ ...s, showAuthForm: true }));
      acceptGiftCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Auto accept gift if user is logged in and "auto" param is true
  useAsyncEffect(async () => {
    if (user && props.params.auto === 'true' && !uiState.isLoading) {
      await handleAcceptClick();
    }
  }, [user, props.params.auto]);

  if (gift.toUserId) {
    return <GiftAlreadyAcceptedPage />;
  }
  const courselabel = courseLabel({ course, tenant: useCurrentTenant(), intl });

  return (
    <GiftLayout>
      <GiftHeader
        onAcceptClick={handleAcceptClick}
        isLoading={uiState.isLoading}
        courseTitle={course.title}
        courseLabel={courselabel}
        customMessage={gift.message}
        fromUserName={gift.fromUserName}
      />
      <CourseDetailsCard course={course} />
      <AcceptGiftCard
        showAuthForm={uiState.showAuthForm}
        forwardRef={acceptGiftCardRef}
        isLoading={uiState.isLoading}
        courseLabel={courselabel}
        onAcceptClick={handleAcceptClick}
        onAuthSubmit={async () => {
          props.router.goto(location.pathname + setUrlSearchParam('auto', 'true').toString());
        }}
      />
      <div class="bg-indigo-50 p-8 rounded-lg border border-indigo-100">
        <h2 class="flex items-center gap-3 text-xl font-bold text-gray-900">
          <IcoCheckCircle class="w-6 h-6 text-indigo-600" />
          <span>{intl('What happens next?')}</span>
        </h2>
        <ol class="mt-4 list-inside list-decimal flex flex-col gap-3 text-gray-700 text-base">
          <li>
            {course.isBundle
              ? intl('Accept the gift to add these courses to your Ruzuku library')
              : intl('Accept the gift to add this course to your Ruzuku library')}
          </li>
          <li>{intl('Access all course materials, videos, and resources immediately')}</li>
          <li>{intl('Join the course community and connect with fellow students')}</li>
          <li>{intl('Learn at your own pace - your course access never expires')}</li>
        </ol>
      </div>
      <footer class="pt-8 text-center">
        <p class="text-sm text-gray-500">
          {course.isBundle
            ? intl('If you have any questions about your gifted courses, please contact,')
            : intl('If you have any questions about your gifted course, please contact,')}{' '}
          <a href="mailto:support@mirasee.com" class="font-medium text-indigo-600 hover:underline">
            support@mirasee.com
          </a>
        </p>
      </footer>
    </GiftLayout>
  );
}

function GiftLayout({ children }: { children: ComponentChildren }) {
  return (
    <>
      <TopBar />
      <div class="min-h-screen bg-gray-50 flex flex-col border-t border-gray-200">
        <main class="mx-auto max-w-4xl w-full flex flex-col gap-8 px-4 py-8">{children}</main>
      </div>
    </>
  );
}

function GiftHeader(props: GiftHeaderProps) {
  return (
    <header class="flex flex-row items-start justify-between gap-6">
      <div class="flex flex-row items-start gap-4 sm:gap-6">
        <div class="flex-shrink-0">
          <IcoGift class="w-15 h-15 text-indigo-600 bg-indigo-100 p-3 rounded-full" />
        </div>
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-gray-900">
            {intl("You've received a gift!")}
          </h1>
          <p class="mt-0 text-base text-gray-600">
            {props.fromUserName} {intl('has gifted you the ')}
            {props.courseLabel} <strong>"{props.courseTitle}"</strong>
            {!!props.customMessage && intl(' and left this personal note:')}
          </p>
          {!!props.customMessage && (
            <div>
              <blockquote class="mt-2 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p class="italic leading-relaxed text-indigo-900">"{props.customMessage}"</p>
              </blockquote>
            </div>
          )}
        </div>
      </div>
      <div class="hidden sm:block flex-shrink-0">
        <BtnPrimary onClick={props.onAcceptClick} isLoading={props.isLoading}>
          {intl('Accept Gift')} →
        </BtnPrimary>
      </div>
    </header>
  );
}

function CourseOutline({ modules }: { modules: OutlineModule[] }) {
  const [openModuleId, setOpenModuleId] = useState<string | undefined>(undefined);

  const toggleModule = (moduleId: string) => {
    setOpenModuleId(openModuleId === moduleId ? undefined : moduleId);
  };

  if (!modules || modules.length === 0) {
    return <p class="mt-4 text-gray-500">{intl('No course outline is available.')}</p>;
  }

  return (
    <div class="mt-4">
      {modules.map((module, index) => {
        const hasLessons = module.lessons && module.lessons.length > 0;
        const lessonsCount = module.lessons ? module.lessons.length : 0;
        const showLessonsText =
          lessonsCount === 1
            ? intl('Show 1 lesson')
            : intl('Show {count:number} lessons', { count: lessonsCount });
        const isExpanded = openModuleId === module.id;

        return (
          <div
            key={module.id}
            class={`py-3 ${index <= modules.length ? 'border-t border-dashed border-gray-300' : ''}`}
          >
            <div class="flex justify-between items-center font-medium">
              <span class=" text-gray-800">
                {index + 1}. {module.title}
              </span>
              {hasLessons && (
                <button
                  onClick={() => toggleModule(module.id)}
                  class="flex items-center text-sm text-gray-600 rounded-md hover:bg-gray-100 p-2 -mr-2 "
                >
                  {!isExpanded && <span class="mr-1">{showLessonsText}</span>}
                  <IcoChevronDown
                    class={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              )}
            </div>
            {isExpanded && hasLessons && (
              <div class="mt-2 pl-6">
                <ul class="list-disc list-inside flex flex-col gap-2 text-gray-600">
                  {module.lessons.map((lesson) => (
                    <li key={lesson.id}>{lesson.title}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
function SubCoursesList({ subcourses }: { subcourses?: SubCourse[] }) {
  if (!subcourses || subcourses.length === 0) {
    return <p class="mt-4 text-gray-500">{intl('No courses are included in this bundle.')}</p>;
  }
  return (
    <div class="mt-4 flex flex-col gap-4">
      {subcourses.map((subcourse) => {
        const src = useImageUrl(subcourse.imagePath);
        return (
          <section
            key={subcourse.id}
            class="flex items-center gap-4 p-3 border border-gray-100 rounded-lg"
          >
            <div class="w-20 h-20 min-w-20 flex items-center justify-center bg-gray-200 rounded-md overflow-hidden">
              {src ? (
                <img
                  class="w-full h-full object-cover"
                  src={`${src}?width=200`}
                  alt={`Course image for ${subcourse.title}`}
                />
              ) : (
                <span class="text-xs text-gray-500">{intl('No Image')}</span>
              )}
            </div>
            <span class="font-semibold text-gray-800 grow">{subcourse.title}</span>
          </section>
        );
      })}
    </div>
  );
}

function CourseDetailsCard({ course }: { course: GiftedCourseWithOutline }) {
  const bannerUrl = useImageUrl(course.bannerPath);
  return (
    <div class=" bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 z-10 relative">
      {bannerUrl ? (
        <div class="h-48 overflow-hidden">
          <img src={bannerUrl} alt={course.title} class="w-full h-full object-cover" />
        </div>
      ) : (
        <div class="h-48 bg-gray-200 flex items-center justify-center">
          <h2 class="text-xl font-bold text-gray-700 p-4 text-center">{course.title}</h2>
        </div>
      )}
      <div class="p-6">
        <h2 class="text-2xl font-bold text-gray-900">{course.title}</h2>
        <p class="mt-1 text-gray-500">
          {intl('Taught by')} <strong>{course.guide.name}</strong>
        </p>

        {course.isBundle ? (
          <>
            <h3 class="mt-6 text-base font-semibold text-gray-800">
              {intl('Courses included in this bundle')}:
            </h3>
            <SubCoursesList subcourses={course.subcourses} />
          </>
        ) : (
          <>
            <h3 class="mt-6 text-base font-semibold text-gray-800">{intl(' Course Overview')}:</h3>
            <CourseOutline modules={course.modules} />
          </>
        )}
      </div>
    </div>
  );
}

function AcceptGiftCard(props: AcceptGiftCardProps) {
  return (
    <div ref={props.forwardRef} class="bg-white p-8 rounded-lg shadow-md border border-gray-200">
      {!props.showAuthForm ? (
        <>
          <h2 class="text-2xl font-bold text-gray-900">{intl('Accept Your Gift')}</h2>
          <p class="mt-2 text-gray-600">
            {intl('To accept this gift and gain access to the ')}
            {props.courseLabel}
            {intl(" ,simply click the button below. You'll be able to start learning right away!")}
          </p>
          <div class="mt-8 flex flex-col gap-4 sm:flex-row">
            <BtnPrimary isLoading={props.isLoading} onClick={props.onAcceptClick}>
              {intl('Accept Gift & Start Learning')} →
            </BtnPrimary>
          </div>
        </>
      ) : (
        <div class="mt-2">
          <AuthForm
            title={intl('Accept gift')}
            subtitle={intl('To accept this gift, enter your email address.')}
            finalActionText={intl('Accept Gift & Start Learning →')}
            onSubmit={props.onAuthSubmit}
          />
        </div>
      )}
    </div>
  );
}

function GiftAlreadyAcceptedPage() {
  return (
    <GiftLayout>
      <div class="py-16 text-center">
        <h1 class="text-3xl font-bold text-gray-900">Gift Already Claimed</h1>
        <p class="mt-4 text-lg text-gray-600">This gift has already been accepted.</p>
        <BtnPrimary href="/" class="mt-8 px-6 py-3">
          Go to Homepage
        </BtnPrimary>
      </div>
    </GiftLayout>
  );
}
